import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  jobsApi,
  type JobListItemDto,
  type ListJobsParams,
} from "@/lib/jobs";
import { JobStatusBadge } from "@/components/job-status-badge";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

// ---------------------------------------------------------------------------
// Filter state
// ---------------------------------------------------------------------------

interface FilterState {
  q: string;
  status: string;
  client: ClientPickerValue | null;
  from: string;
  to: string;
}

const EMPTY_FILTERS: FilterState = {
  q: "",
  status: "",
  client: null,
  from: "",
  to: "",
};

const JOB_STATUSES = [
  "Pending",
  "InProgress",
  "OnHold",
  "Completed",
  "Cancelled",
  "Invoiced",
];

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// Build API params from filter state
// ---------------------------------------------------------------------------

function buildParams(state: FilterState, pageIndex: number): ListJobsParams {
  return {
    q: state.q || undefined,
    status: state.status || undefined,
    clientId: state.client?.clientId,
    from: state.from || undefined,
    to: state.to || undefined,
    page: pageIndex,
    pageSize: 50,
  };
}

// ---------------------------------------------------------------------------
// JobsListPage
// ---------------------------------------------------------------------------

export function JobsListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [pageIndex, setPageIndex] = useState(0);

  const params = useMemo(() => buildParams(filters, pageIndex), [filters, pageIndex]);

  const { data, isLoading } = useQuery({
    queryKey: ["jobs", params],
    queryFn: () => jobsApi.list(params),
  });

  const visibleRows = useMemo(() => data?.items ?? [], [data]);

  function updateFilters(patch: Partial<FilterState>) {
    setFilters((f) => ({ ...f, ...patch }));
    setPageIndex(0);
  }

  const cols: DataGridColumn<JobListItemDto>[] = [
    {
      id: "ref",
      header: "Job #",
      accessor: (r) => (
        <Link to={`/jobs/${r.jobId}`} className="underline text-primary">
          {r.jobReferenceNo}
        </Link>
      ),
    },
    {
      id: "client",
      header: "Client",
      accessor: (r) => r.clientName ?? "—",
    },
    {
      id: "owner",
      header: "Owner",
      accessor: (r) => r.ownerEmployeeName ?? "—",
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => (
        <JobStatusBadge code={r.currentStatusCode} color={r.statusColor} />
      ),
    },
    {
      id: "dispatch",
      header: "Expected dispatch",
      accessor: (r) =>
        r.expectedDispatchDate
          ? new Date(r.expectedDispatchDate).toLocaleDateString("en-AU")
          : "—",
    },
    {
      id: "total",
      header: "Total (inc. GST)",
      accessor: (r) => (r.totalIncGst != null ? fmt.format(r.totalIncGst) : "—"),
      align: "right",
    },
  ];

  return (
    <ListPage
      title="Jobs"
      primaryAction={
        <IntentButton onClick={() => navigate("/jobs/new")} aria-label="New job">
          + New Job
        </IntentButton>
      }
      filterBar={
        <div className="space-y-3">
          {/* Status filter pills */}
          <div className="flex flex-wrap gap-1">
            <span className="self-center text-xs text-muted-foreground mr-1">Status:</span>
            <button
              type="button"
              onClick={() => updateFilters({ status: "" })}
              className={`inline-flex h-7 items-center rounded-md px-2 text-xs font-medium border ${
                filters.status === ""
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground border-border hover:bg-accent"
              }`}
            >
              All
            </button>
            {JOB_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => updateFilters({ status: s })}
                aria-pressed={filters.status === s}
                className={`inline-flex h-7 items-center rounded-md px-2 text-xs font-medium border ${
                  filters.status === s
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-accent"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Filter inputs */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="jobs-q">Search ref, description, PO #</Label>
              <Input
                id="jobs-q"
                value={filters.q}
                onChange={(e) => updateFilters({ q: e.target.value })}
                placeholder="Search…"
                aria-label="Search jobs"
              />
            </div>
            <div>
              <Label>Client</Label>
              <ClientPicker
                value={filters.client}
                onChange={(v) => updateFilters({ client: v })}
              />
            </div>
            <div>
              <Label htmlFor="jobs-from">Expected dispatch from</Label>
              <Input
                id="jobs-from"
                type="date"
                value={filters.from}
                onChange={(e) => updateFilters({ from: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="jobs-to">Expected dispatch to</Label>
              <Input
                id="jobs-to"
                type="date"
                value={filters.to}
                onChange={(e) => updateFilters({ to: e.target.value })}
              />
            </div>
          </div>
        </div>
      }
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.jobId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={pageIndex}
        pageSize={50}
        loading={isLoading}
        emptyHeadline="No jobs match these filters."
        onPageChange={setPageIndex}
      />
    </ListPage>
  );
}
